import { useState, useEffect, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { Layout, Alert } from 'antd';
import { observer } from 'mobx-react-lite';
import { Sidebar } from '../../widgets/sidebar/Sidebar';
import { AppHeader } from '../../widgets/header/AppHeader';
import { useRootStore } from '../providers/StoreProvider';
import { globalChatStore } from '../../entities/crm/chat-store';
import { shellChrome } from '../../shared/stores/shell-chrome';
import { useRolloutFlag } from '../../features/cockpit-r3/useCockpitFlag';
import { isStaffAccount, platformNewCabinet, staffPrefersLegacy } from '../../shared/stores/new-cabinet';
import { R3ShellHeader } from '../../features/cockpit-r3/R3Header';

const { Content, Sider } = Layout;

const MOBILE_BREAKPOINT = 768;

/**
 * Основной лейаут приложения — сайдбар слева, хедер сверху, контент.
 * На мобильных сайдбар автоматически свёрнут и показывается как оверлей.
 */
export const MainLayout = observer(function MainLayout() {
  const rootStore = useRootStore();
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const activePlatformId = rootStore.platforms.activePlatformId;
  // Пилот R3: во всех разделах — шапка R3 с выпадающим меню вместо
  // сайдбара и старой шапки. Тот же флаг, что у Cockpit (раскатка по
  // учёткам и объектам); OFF — прежняя оболочка. Пока флаг не прочитан
  // (null), оболочку не рисуем, чтобы не мигала старая.
  const pilotShell = useRolloutFlag(
    Number(rootStore.profile?.id) || 0,
    activePlatformId || 0,
    '/cockpit-r3.json',
    false,
  );
  // Отель на новом кабинете (поле отеля) — R3 сразу, без файла пилота.
  // Суперадмин и поддержка — новый интерфейс всегда (кроме «Старый
  // интерфейс» в меню профиля), в том числе без выбранного отеля.
  const staffR3 = isStaffAccount() && !staffPrefersLegacy();
  const r3Shell = staffR3 || platformNewCabinet(activePlatformId) ? true : pilotShell;
  const legacyShell = r3Shell === false;
  // Пока не знаем учётку и объект, флаг пилота считается «ещё не прочитан»:
  // иначе страницы с двумя видами успевают увести на старый экран.
  const idsKnown = !!rootStore.profile?.id && (!!activePlatformId || staffR3);
  useEffect(() => {
    shellChrome.setR3(idsKnown ? r3Shell ?? null : null);
  }, [r3Shell, idsKnown]);
  const sidebarHidden = !legacyShell || shellChrome.sidebarHidden;

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const handler = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      if (e.matches) setCollapsed(true);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Подключаем чат-WS на уровне всего приложения (а не только на стр. CRM),
  // чтобы уведомления о новых сообщениях гостей всплывали на любой странице.
  // При смене активной площадки в селекторе — переподключаемся к её группе.
  useEffect(() => {
    globalChatStore.reconnectWebSocket();
    return () => globalChatStore.disconnectWebSocket();
  }, [activePlatformId]);

  const handleToggle = useCallback(() => setCollapsed((c) => !c), []);

  // На мобильных — при клике по пункту меню сворачиваем сайдбар
  const handleMobileClose = useCallback(() => {
    if (isMobile) setCollapsed(true);
  }, [isMobile]);

  return (
    <Layout className="min-h-screen">
      {/* Оверлей на мобильных при открытом сайдбаре */}
      {isMobile && !collapsed && !sidebarHidden && (
        <div
          onClick={() => setCollapsed(true)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 49,
            background: 'rgba(0,0,0,0.3)',
          }}
        />
      )}

      {/* Сайдбар. На Cockpit R3 скрыт: там меню выпадающее. */}
      {!sidebarHidden && (
      <Sider
        collapsible
        collapsed={isMobile ? false : collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        width={240}
        collapsedWidth={isMobile ? 0 : 64}
        theme="dark"
        className="fixed left-0 top-0 bottom-0 z-50 overflow-auto"
        style={{
          background: 'var(--color-sidebar-bg)',
          transform: isMobile && collapsed ? 'translateX(-240px)' : 'translateX(0)',
          transition: 'transform var(--transition-normal)',
        }}
      >
        <Sidebar collapsed={isMobile ? false : collapsed} onNavigate={handleMobileClose} />
      </Sider>
      )}

      {/* Основная область */}
      <Layout
        style={{
          marginLeft: isMobile || sidebarHidden ? 0 : (collapsed ? 64 : 240),
          transition: 'margin-left var(--transition-normal)',
        }}
      >
        {!shellChrome.headerHidden && legacyShell && (
          <AppHeader collapsed={collapsed} onToggle={handleToggle} />
        )}
        {!shellChrome.headerHidden && r3Shell === true && <R3ShellHeader />}

        {/* Баннер триала */}
        <TrialBanner />

        <Content
          className="p-6"
          style={{
            minHeight: `calc(100vh - var(--header-height))`,
            background: r3Shell ? '#f1f4f8' : 'var(--color-bg-secondary)',
          }}
        >
          <Outlet key={activePlatformId} />
        </Content>
      </Layout>
    </Layout>
  );
});

/**
 * Баннер о тестовом периоде / истечении подписки.
 * Читает trial_info из активной платформы.
 */
const TrialBanner = observer(function TrialBanner() {
  const rootStore = useRootStore();
  const platform = rootStore.platforms.activePlatform;
  const trialInfo = platform?.trial_info;

  if (!trialInfo) return null;

  const { is_trial, days_left, is_expired, payment_status } = trialInfo;

  // Заблокирован
  if (payment_status === 2) {
    return (
      <Alert
        message="Доступ ограничен"
        description="Объект заблокирован. Обратитесь к администратору для восстановления доступа."
        type="error"
        showIcon
        banner
      />
    );
  }

  // Триал истёк
  if (is_trial && is_expired) {
    return (
      <Alert
        message="Тестовый период завершён"
        description="Функции ограничены. Оформите подписку для полного доступа ко всем возможностям."
        type="warning"
        showIcon
        banner
      />
    );
  }

  // Триал заканчивается скоро (≤ 3 дня)
  if (is_trial && days_left !== null && days_left <= 3 && days_left >= 0) {
    return (
      <Alert
        message={`Тестовый период заканчивается через ${days_left} ${days_left === 1 ? 'день' : days_left <= 4 ? 'дня' : 'дней'}`}
        description="После завершения тестового периода доступ к некоторым функциям будет ограничен."
        type="info"
        showIcon
        banner
        closable
      />
    );
  }

  return null;
});
